import {organisationsConfigStore} from '@store/settings/organisationsConfig/organisationsConfig.store';
import APIAxios, {APIRoutes} from '@api/axios.api';
import {catchError, from, map, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import SnackError from '@utils/error.utils';
import {OrganisationConfigPagination} from '@store/settings/organisationsConfig/organisationsConfig.model';
import {Pagination} from '@store/common/pagination.model';
import {setEntities} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {getOrganisationsConfigDataSource} from '@store/settings/organisationsConfig/organisationsConfig.requests';

export class OrganisationsConfigService {
  store = organisationsConfigStore;

  resetStore = () => this.store.reset();

  setFilters = (name: string) => (value: string) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    }
  }));

  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    }
  }));

  getOrganisationsConfig = () => {
    const {tablePagination, filters: {search}} = this.store.getValue();
    return from(APIAxios({
      ...APIRoutes.GETAllOrganisationsConfig(),
      params: {
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<OrganisationConfigPagination>) => {
          return response.data;
        }),
        tap((organisationPagination) => {
          const paginationData = Pagination.toPaginationData(organisationPagination, tablePagination.page);

          this.store.update(
            setEntities(organisationPagination.data),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, organisationPagination.data.map((organisation) => organisation.id)),
            getOrganisationsConfigDataSource.setSuccess(),
          );
        }),
        getOrganisationsConfigDataSource.trackRequestStatus(),
      );
  };

  updateOrganisationConfig(organisationId: number, data: { isSupplier?: boolean; isClient?: boolean }) {
    return from(
      APIAxios({
        ...APIRoutes.PATCHOrganisationConfig(organisationId),
        data: {
          isSupplier: data.isSupplier,
          isClient: data.isClient,
        }
      }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getOrganisationsConfig())
      );
  }
}

export const organisationsConfigService = new OrganisationsConfigService();
